<template>
  <span
    class="myhome-footer__item dea-bg-white"
    :class="[accentClass, { 'myhome-footer__item--active': checkRouteName, 'myhome-footer__link--disabled': loading }]"
    @click.capture="changeRoute"
  >
    <component
      :is="buttonComponent"
      :id="`myhome-${routeName}`"
      class="myhome-footer__link"
      :class="accentClass"
      :to="to"
      :href="href"
      :disabled="disabled"
    >
      <div class="myhome-footer__content">
        <img
          v-if="iconFile"
          :src="iconFile"
          class="myhome-footer__icon-image"
          :class="logoClass"
          :height="variables.bottomBarLogoHeight"
          alt=""
        />
        <!-- eslint-disable vue/no-v-html -->
        <span
          v-else-if="iconSvg"
          class="myhome-footer__icon-svg"
          :class="logoClass"
          aria-hidden="true"
          v-html="iconSvg"
        ></span>
        <!-- eslint-enable vue/no-v-html -->
        <span v-else-if="icon" class="myhome-footer__icon" :class="`dea-ico-${icon}`" aria-hidden="true"></span>
        <span class="myhome-footer__item-label dea-heading--medium__xm">
          {{ label }}
        </span>
      </div>
    </component>
    <div v-if="notifications" class="myhome-footer__notification dea-heading--regular__xs">{{ notifications }}</div>
  </span>
</template>

<script>
import variables from "~/scss/styles.scss";

export default {
  name: "BottomBarButton",
  props: {
    routeName: {
      type: String,
      default: null,
    },
    href: {
      type: String,
      default: "",
    },
    label: {
      type: String,
      required: true,
    },
    accentClass: {
      type: String,
      required: true,
    },
    bgAccentClass: {
      type: String,
      default: "",
    },
    icon: {
      type: String,
      default: "",
    },
    loading: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: [Boolean, Number],
      default: false,
    },
    iconFile: {
      type: String,
      default: "",
    },
    iconSvg: {
      type: String,
      default: "",
    },
    notifications: {
      type: Number,
      default: null,
    },
    logoClass: {
      type: String,
      default: "",
    },
  },
  data() {
    return { variables };
  },
  computed: {
    buttonComponent() {
      return this.routeName ? "router-link" : "a";
    },
    checkRouteName() {
      return (
        this.$route.name === this.routeName ||
        this.$router.currentRoute.matched.some(({ name }) => name === this.routeName)
      );
    },
    to() {
      return this.routeName ? { name: this.routeName } : null;
    },
  },
  methods: {
    changeRoute(event) {
      // Accessibility - Need this to focus on the first link of the header during route change
      // Get custom header element shadow root
      const customEl = document.querySelector(".dea-top-header--deascuola");

      if (customEl && customEl.shadowRoot) {
        const { shadowRoot } = customEl;
        // Get the first link of the header
        const focusable = shadowRoot.querySelector(".dea-header-menu > li > a");
        // Focus the element
        focusable.focus();
      }

      const link = event.currentTarget.querySelector("a");
      if (link && link.getAttribute("disabled")) {
        event.preventDefault();
      }
    },
  },
};
</script>

<style lang="scss">
@import "@/styles/system.utils";
@import "~/scss/_local-variables"; //sass-lint:disable-line clean-import-paths

$notification-icon: 20px;
$notification-padding: 1px;

.myhome-footer {
  &__item {
    @include media(">m") {
      height: 60px;
      margin-top: $space-small;
    }
    cursor: pointer;
    flex: 1;
    height: 100%;
    max-width: $bottom-bar-max-width;
    position: relative;
    text-align: center;

    &--active {
      border-bottom: $border-extra-bold;
    }

    &:first-child {
      @include media(">m") {
        border-top-left-radius: $border-radius-large;
      }
    }

    &:last-child {
      @include media(">m") {
        border-top-right-radius: $border-radius-large;
      }
    }
  }

  &__icon-image {
    @include media("<=m") {
      margin-right: 0;
    }
    margin: $space-xxx-small $space-small 1px 0;
  }

  &__icon-svg {
    @include media("<=m") {
      margin-right: 0;
    }
    margin: $space-xxx-small $space-small 1px 0;

    svg {
      height: $bottom-bar-logo-height;
      width: auto;
    }
  }

  &__link {
    border-right: $border-light $dea-color-grey-light;
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;

    &[disabled] {
      display: inline-block;
      opacity: $opacity-disabled;
    }

    &--disabled {
      animation: loadingAnimation $loading-animation-speed $loading-animation-duration;
    }
  }

  &__content {
    @include media(">m") {
      align-items: center;
      display: flex;
      padding-left: $space-small !important; //sass-lint:disable-line no-important
    }
    height: 100%;
  }

  &__icon {
    @include media(">m") {
      margin-right: $space-small;
    }

    &:before {
      font-size: $h-font-size-x-large;
    }
  }

  &__item-label {
    display: block;
    text-align: center;
  }

  &__notification {
    background-color: $dea-color-warning-message;
    border-radius: $border-radius-circle;
    color: $dea-color-white;
    height: $notification-icon;
    line-height: $notification-icon;
    padding: $notification-padding $space-xx-small;
    position: absolute;
    right: $space-xx-small;
    top: $space-xx-small;
    width: $notification-icon;
  }
}
</style>
